import type { LifecycleEntry } from '@ministry/ui/internal';

/**
 * Bilingual `StatusBadge` entries for the administration screens — same
 * `{token, icon, en, ne}` shape `lib/appointment-lifecycle.ts`/`lib/availability`
 * already use, so a badge in `/admin/*` reads exactly like a badge anywhere else in
 * this app, never a second visual language.
 */

const ACCOUNT_STATUS_ENTRY: Record<string, LifecycleEntry> = {
  active: { token: 'good', icon: 'check-circle', en: 'Active', ne: 'सक्रिय' },
  suspended: { token: 'warning', icon: 'alert-triangle', en: 'Suspended', ne: 'निलम्बित' },
  deactivated: { token: 'critical', icon: 'x-circle', en: 'Deactivated', ne: 'निष्क्रिय पारिएको' },
};

export function accountStatusEntry(status: string): LifecycleEntry {
  return ACCOUNT_STATUS_ENTRY[status] ?? { token: 'neutral', icon: 'circle-minus', en: status, ne: status };
}

const ASSIGNMENT_LIVE_ENTRY: LifecycleEntry = { token: 'good', icon: 'check-circle', en: 'Live', ne: 'हालको' };
const ASSIGNMENT_LAPSED_ENTRY: LifecycleEntry = { token: 'neutral', icon: 'clock-x', en: 'Ended', ne: 'समाप्त' };

export function assignmentLiveEntry(live: boolean): LifecycleEntry {
  return live ? ASSIGNMENT_LIVE_ENTRY : ASSIGNMENT_LAPSED_ENTRY;
}

const ORG_UNIT_LIVE_ENTRY: LifecycleEntry = { token: 'good', icon: 'check-circle', en: 'Active', ne: 'सक्रिय' };
const ORG_UNIT_ARCHIVED_ENTRY: LifecycleEntry = { token: 'neutral', icon: 'archive-box', en: 'Archived', ne: 'सुरक्षित गरिएको' };

export function orgUnitStatusEntry(archivedAt: string | null): LifecycleEntry {
  return archivedAt === null ? ORG_UNIT_LIVE_ENTRY : ORG_UNIT_ARCHIVED_ENTRY;
}

const TENANT_STATUS_ENTRY: Record<string, LifecycleEntry> = {
  active: { token: 'good', icon: 'check-circle', en: 'Active', ne: 'सक्रिय' },
  suspended: { token: 'critical', icon: 'alert-triangle', en: 'Suspended', ne: 'निलम्बित' },
};

export function tenantStatusEntry(status: string): LifecycleEntry {
  return TENANT_STATUS_ENTRY[status] ?? { token: 'neutral', icon: 'circle-minus', en: status, ne: status };
}

const ORG_UNIT_KIND_LABEL: Record<string, { en: string; ne: string }> = {
  ministry: { en: 'Ministry', ne: 'मन्त्रालय' },
  division: { en: 'Division', ne: 'महाशाखा' },
  section: { en: 'Section', ne: 'शाखा' },
};

export function orgUnitKindLabel(kind: string, locale: 'en' | 'ne'): string {
  const entry = ORG_UNIT_KIND_LABEL[kind];
  if (!entry) return kind;
  return locale === 'ne' ? entry.ne : entry.en;
}

const SCOPE_LEVEL_LABEL: Record<string, { en: string; ne: string }> = {
  own: { en: 'Own records only', ne: 'आफ्नो मात्र' },
  section: { en: 'Section', ne: 'शाखा' },
  division: { en: 'Division', ne: 'महाशाखा' },
  ministry: { en: 'Ministry-wide', ne: 'मन्त्रालयभर' },
  platform: { en: 'Platform-wide', ne: 'प्लेटफर्मभर' },
};

export function scopeLevelLabel(level: string, locale: 'en' | 'ne'): string {
  const entry = SCOPE_LEVEL_LABEL[level];
  if (!entry) return level;
  return locale === 'ne' ? entry.ne : entry.en;
}
